import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Trophy, RotateCcw, Home, CheckCircle, XCircle, MinusCircle, Play, Pause, Volume2, Image as ImageIcon, ZoomIn } from 'lucide-react';

import { convertToTCFScore, getCECRLLevelCE, getCECRLLevelCO } from '../utils/tcfScoring';
import { getMediaUrl } from '../services/api';
import { ImageLightbox } from '../components/ImageLightbox';

// Messages de feedback basés sur le niveau
const getLevelMessage = (level) => {
  if (level.startsWith('C2')) return 'Excellent ! Niveau avancé supérieur.';
  if (level.startsWith('C1')) return 'Très bien ! Niveau avancé.';
  if (level.startsWith('B2')) return 'Bien ! Niveau intermédiaire avancé.';
  if (level.startsWith('B1')) return 'Assez bien. Niveau intermédiaire.';
  if (level.startsWith('A2')) return 'À améliorer. Niveau élémentaire.';
  return 'Ne vous découragez pas ! La pratique régulière vous aidera à progresser.';
};

// Question Review Component - Affiche TOUT directement
const QuestionReview = ({ question, index, userAnswer, status }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const audioRef = useRef(null);

  const handlePlayAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  return (
    <div className={`bg-white rounded-xl border-2 overflow-hidden shadow-sm ${
      status === 'correct' ? 'border-green-400' :
      status === 'incorrect' ? 'border-red-400' :
      'border-gray-300'
    }`}>
      {/* Header avec numéro et statut */}
      <div className={`px-4 py-3 flex items-center justify-between ${
        status === 'correct' ? 'bg-green-50' :
        status === 'incorrect' ? 'bg-red-50' :
        'bg-gray-50'
      }`}>
        <div className="flex items-center space-x-3">
          <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold text-white ${
            question.level === 'A1' ? 'bg-green-500' :
            question.level === 'A2' ? 'bg-blue-500' :
            question.level === 'B1' ? 'bg-yellow-500' :
            question.level === 'B2' ? 'bg-orange-500' :
            question.level === 'C1' ? 'bg-red-500' :
            'bg-purple-500'
          }`}>
            {index + 1}
          </div>
          <div>
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
              question.level === 'A1' ? 'bg-green-100 text-green-700' :
              question.level === 'A2' ? 'bg-blue-100 text-blue-700' :
              question.level === 'B1' ? 'bg-yellow-100 text-yellow-700' :
              question.level === 'B2' ? 'bg-orange-100 text-orange-700' :
              question.level === 'C1' ? 'bg-red-100 text-red-700' :
              'bg-purple-100 text-purple-700'
            }`}>
              {question.level} • {question.points}pt
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {status === 'correct' && (
            <span className="flex items-center space-x-1 text-green-600 font-semibold text-sm">
              <CheckCircle className="h-5 w-5" />
              <span>Correct</span>
            </span>
          )}
          {status === 'incorrect' && (
            <span className="flex items-center space-x-1 text-red-600 font-semibold text-sm">
              <XCircle className="h-5 w-5" />
              <span>Incorrect</span>
            </span>
          )}
          {status === 'unanswered' && (
            <span className="flex items-center space-x-1 text-gray-500 font-semibold text-sm">
              <MinusCircle className="h-5 w-5" />
              <span>Non répondue</span>
            </span>
          )}
        </div>
      </div>

      {/* Contenu principal */}
      <div className="p-4">
        {/* Instruction/Question */}
        <p className="font-medium text-gray-800 mb-4 text-base">
          {question.instruction || "Question sans instruction"}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Colonne gauche - Média (Image + Audio) */}
          <div className="space-y-4">
            {/* Image */}
            {question.imageUrl ? (
              <div
                className="group relative bg-gray-100 rounded-lg overflow-hidden cursor-zoom-in"
                onClick={() => setLightboxImage(getMediaUrl(question.imageUrl))}
                role="button"
                aria-label="Agrandir l'image"
              >
                <img
                  src={getMediaUrl(question.imageUrl)}
                  alt={`Question ${index + 1}`}
                  className="w-full h-auto max-h-[250px] object-contain"
                  onContextMenu={(e) => e.preventDefault()}
                  draggable={false}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
                    <ZoomIn className="h-5 w-5 text-gray-700" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-400">
                <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Pas d'image</p>
              </div>
            )}

            {/* Audio Player */}
            {question.audioUrl ? (
              <div className="bg-gray-100 rounded-lg p-3">
                <audio
                  ref={audioRef}
                  src={getMediaUrl(question.audioUrl)}
                  onEnded={handleAudioEnded}
                  controls
                  controlsList="nodownload"
                  className="w-full h-10"
                />
              </div>
            ) : (
              <div className="bg-gray-100 rounded-lg p-4 text-center text-gray-400">
                <Volume2 className="h-8 w-8 mx-auto mb-1 opacity-50" />
                <p className="text-sm">Pas d'audio</p>
              </div>
            )}
          </div>

          {/* Colonne droite - Les 4 Propositions */}
          <div className="space-y-2">
            <p className="font-semibold text-gray-700 text-sm mb-2">Les 4 propositions :</p>
            {['A', 'B', 'C', 'D'].map((opt) => {
              const isCorrect = question.correctAnswer === opt;
              const isUserAnswer = userAnswer === opt;
              const optionText = question[`option${opt}`] || `Option ${opt}`;
              
              return (
                <div
                  key={opt}
                  className={`p-3 rounded-lg border-2 ${
                    isCorrect 
                      ? 'border-green-500 bg-green-50' 
                      : isUserAnswer && !isCorrect
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                      isCorrect 
                        ? 'bg-green-500 text-white' 
                        : isUserAnswer && !isCorrect
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {opt}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm ${isCorrect ? 'font-semibold text-green-700' : 'text-gray-700'}`}>
                        {optionText}
                      </span>
                    </div>
                    {isCorrect && (
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    )}
                    {isUserAnswer && !isCorrect && (
                      <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    )}
                  </div>
                  {isCorrect && (
                    <p className="text-xs text-green-600 mt-1 ml-10 font-medium">✓ Bonne réponse</p>
                  )}
                  {isUserAnswer && !isCorrect && (
                    <p className="text-xs text-red-600 mt-1 ml-10 font-medium">✗ Votre réponse</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <ImageLightbox
        src={lightboxImage}
        alt={`Question ${index + 1}`}
        onClose={() => setLightboxImage(null)}
      />
    </div>
  );
};

export default function ExerciseResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    moduleName = 'comprehension_orale',
    serieName = 'Série',
    serieId,
    questions = [],
    answers = {},
    score = 0,
    maxPoints = 699,
    timeSpent = 0
  } = location.state || {};

  // Convertir le score brut en score TCF (0-699)
  const tcfScore = convertToTCFScore(score, maxPoints || 699);
  
  // Obtenir le niveau en fonction du module
  let nclcResultData;
  if (moduleName === 'comprehension_orale') {
    nclcResultData = getCECRLLevelCO(tcfScore);
  } else {
    nclcResultData = getCECRLLevelCE(tcfScore);
  }
  
  const nclcResult = {
    level: nclcResultData.cecrl,
    nclc: nclcResultData.nclc,
    message: getLevelMessage(nclcResultData.cecrl)
  };

  const correctCount = Object.keys(answers).filter(key => {
    const q = questions[parseInt(key)];
    return q && answers[key] === q.correctAnswer;
  }).length;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) return `${mins} min ${secs} sec`;
    return `${secs} sec`;
  };

  const getQuestionStatus = (index) => {
    const question = questions[index];
    if (!question) return 'unanswered';
    const userAnswer = answers[index];
    if (!userAnswer) return 'unanswered';
    return userAnswer === question.correctAnswer ? 'correct' : 'incorrect';
  };

  const handleRetry = () => {
    navigate(`/modules/${moduleName.replace('_', '-')}/exercise?serie=${serieId}`);
  };

  const handleBackToSeries = () => {
    navigate(`/modules/${moduleName.replace('_', '-')}`);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6 animate-in pb-8 px-4">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white p-6 rounded-2xl text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="results-title">Examen Terminé !</h1>
          <p className="text-blue-100">{serieName}</p>
        </div>

        {/* Main Score Card */}
        <div className="bg-gradient-to-r from-yellow-100 to-amber-100 border-2 border-yellow-300 rounded-2xl p-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Trophy className="h-8 w-8 text-yellow-600" />
            <h2 className="text-3xl sm:text-4xl font-bold text-yellow-600" data-testid="nclc-level">
              Niveau {nclcResult.level}
            </h2>
          </div>
          <p className="text-xl font-semibold text-gray-700 mb-1">
            NCLC {nclcResult.nclc}
          </p>
          <p className="text-2xl font-bold text-gray-800" data-testid="score-display">
            {tcfScore} / 699 points (Score brut: {score}/{maxPoints})
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-green-50 p-4 rounded-xl text-center border border-green-200">
            <div className="flex items-center justify-center space-x-2 mb-1">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-xl sm:text-2xl font-bold text-green-600" data-testid="correct-count">{correctCount}</span>
            </div>
            <p className="text-xs sm:text-sm text-green-600">Correctes</p>
          </div>
          
          <div className="bg-pink-50 p-4 rounded-xl text-center border border-pink-200">
            <span className="text-xl sm:text-2xl font-bold text-pink-600">
              {questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0}%
            </span>
            <p className="text-xs sm:text-sm text-pink-600">Réussite</p>
          </div>
          
          <div className="bg-orange-50 p-4 rounded-xl text-center border border-orange-200">
            <span className="text-xl sm:text-2xl font-bold text-orange-600">{formatTime(timeSpent)}</span>
            <p className="text-xs sm:text-sm text-orange-600">Temps</p>
          </div>
        </div>

        {/* Feedback Message */}
        <div className={`p-4 rounded-xl ${
          nclcResult.level.startsWith('C') ? 'bg-green-100 border-l-4 border-green-500' :
          nclcResult.level.startsWith('B') ? 'bg-blue-100 border-l-4 border-blue-500' :
          'bg-red-100 border-l-4 border-red-500'
        }`}>
          <p className="text-sm text-gray-700">{nclcResult.message}</p>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={handleRetry}
            data-testid="retry-btn"
            className="flex items-center justify-center space-x-2 py-4 rounded-xl bg-blue-500 text-white hover:bg-blue-600 font-semibold transition-colors"
          >
            <RotateCcw className="h-5 w-5" />
            <span>Refaire l'examen</span>
          </button>
          
          <button
            onClick={handleBackToSeries}
            data-testid="back-btn"
            className="flex items-center justify-center space-x-2 py-4 rounded-xl border-2 border-gray-300 hover:bg-gray-100 font-semibold transition-colors"
          >
            <Home className="h-5 w-5" />
            <span>Retour aux séries</span>
          </button>
        </div>

        {/* Quick Answer Grid */}
        <div className="bg-white p-4 rounded-xl border shadow-sm">
          <h3 className="font-semibold mb-3">Aperçu rapide</h3>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {questions.map((_, index) => {
              const status = getQuestionStatus(index);
              return (
                <div
                  key={index}
                  className={`h-8 w-8 rounded flex items-center justify-center font-semibold text-xs ${
                    status === 'correct' ? 'bg-green-500 text-white' :
                    status === 'incorrect' ? 'bg-red-500 text-white' :
                    'bg-gray-200 text-gray-500'
                  }`}
                >
                  {index + 1}
                </div>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="flex items-center justify-center space-x-4 text-xs">
            <div className="flex items-center space-x-1">
              <div className="h-3 w-3 rounded bg-green-500" />
              <span>Correct</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="h-3 w-3 rounded bg-red-500" />
              <span>Incorrect</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="h-3 w-3 rounded bg-gray-300" />
              <span>Non répondue</span>
            </div>
          </div>
        </div>

        {/* Detailed Review Section - Affiche TOUT */}
        <div className="space-y-4">
          <h3 className="text-xl font-bold">
            Revue détaillée
            <span className="text-sm font-normal text-gray-500 ml-2">
              (Questions, images, audio, propositions et bonnes réponses)
            </span>
          </h3>
          
          <div className="space-y-4">
            {questions.map((question, index) => (
              <QuestionReview
                key={index}
                question={question}
                index={index}
                userAnswer={answers[index]}
                status={getQuestionStatus(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
